import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {Decimal} from 'decimal.js';
import React, {useState} from 'react';

import {coreObjectArchiveAction} from 'app-core/platform/archivable/CoreObjectArchiveActions';
import {getUnarchiveAction} from 'archive/ArchiveActionsUtil';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import FieldActionButtons from 'metadata/FieldActionButtons';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARKER, GRAY_LIGHT} from 'ui-library/Colors';

import {UnitTypesQuery} from './__generated/UnitTypesQuery';
import UnitArchiveWarningBanner from './UnitArchiveWarningBanner';
import {CreateUnitParams, UpdateUnitParams} from './UnitDictionaryDAO';
import UnitTableRowForEditingOrCreation from './UnitTableRowForEditingOrCreation';
import {isUnitDictionaryManagementEnabledForUser} from './util';

type Unit = UnitTypesQuery['unitTypes'][number]['units'][number];
type BaseUnit = UnitTypesQuery['unitTypes'][number]['baseUnit'];

export interface UnitTableRowProps {
  unit?: Unit;
  baseUnit: BaseUnit;
  unitTypeId: string;
  onSave: (params: CreateUnitParams | UpdateUnitParams) => Promise<void>;
  onCancel?: () => void;
  refetch: () => void;
  highlightName?: boolean;
  highlightSymbol?: boolean;
}

export default function UnitTableRow({
  unit,
  baseUnit,
  unitTypeId,
  onSave,
  onCancel,
  refetch,
  highlightName,
  highlightSymbol,
}: UnitTableRowProps): JSX.Element {
  const forCreation = unit == null;
  const [isEditing, setIsEditing] = useState(false);

  const handleCancel = (): void => {
    if (onCancel != null) {
      onCancel();
    }
    setIsEditing(false);
  };

  const handleSave = (params: CreateUnitParams | UpdateUnitParams): void => {
    onSave(params);
    setIsEditing(false);
  };

  if (isEditing || forCreation) {
    return (
      <UnitTableRowForEditingOrCreation
        unit={unit}
        baseUnit={baseUnit}
        onSave={handleSave}
        onCancel={handleCancel}
        unitTypeId={unitTypeId}
      />
    );
  }

  const baseUnitSymbol = baseUnit.symbol;
  const isBaseUnit = unit.id === baseUnit.id;
  const conversionFactor = new Decimal(unit.conversionFactor);

  const handleClickEdit = (): void => {
    setIsEditing(true);
  };
  const onArchive = (): void => {
    ArchiveModal.open({
      items: [unit],
      onArchive: (itemIds: Array<string>, purpose: string) => {
        coreObjectArchiveAction(itemIds, purpose);
        refetch();
      },
      renderArchivableItemTableOverride: () => (
        <UnitArchiveWarningBanner unitIds={[unit.id]} entityLabel="unit" />
      ),
    });
  };
  const onUnarchive = async (): Promise<void> => {
    const unarchiveFn = getUnarchiveAction([unit.id]);
    await unarchiveFn([unit.id]);
    refetch();
  };

  return (
    <div
      className={css(styles.rowContainer)}
      {...getTestProps({component: 'UnitTableRow', element: 'root', key: unit.id})}
    >
      <div
        className={classNames('simpleDivTable-row', {[css(styles.archivedField)]: unit.archiveData.archived})}
      >
        <div className="simpleDivTable-cell--narrow">
          <div className="simpleDivTable-cell-contents">
            <span
              className={classNames({[css(styles.highlight)]: highlightName})}
              {...getTestProps({component: 'UnitTableRow', element: 'nameField'})}
            >
              {unit.name} {unit.archiveData.archived && `(${unit.archiveData.purpose})`}
            </span>
          </div>
        </div>

        <div className="simpleDivTable-cell simpleDivTable-cell--narrow">
          <div className="simpleDivTable-cell-contents">
            <span
              className={classNames({[css(styles.highlight)]: highlightSymbol})}
              {...getTestProps({component: 'UnitTableRow', element: 'symbolField'})}
            >
              {unit.symbol}
            </span>
          </div>
        </div>

        <div className="simpleDivTable-cell">
          <div className="simpleDivTable-cell-contents--last">
            {isBaseUnit ? (
              <span className={css(styles.baseUnitText)}>Base unit</span>
            ) : (
              <span
                {...getTestProps({component: 'UnitTableRow', element: 'conversionFactorField'})}
              >{`${conversionFactor.toString()} x ${baseUnitSymbol}`}</span>
            )}
          </div>
        </div>

        {isUnitDictionaryManagementEnabledForUser() && (
          <div className="simpleDivTable-actionGroupContainer u-spacing-right">
            <FieldActionButtons
              isEditing={isEditing}
              cancel={handleCancel}
              edit={unit.archiveData.archived ? undefined : handleClickEdit}
              archive={isBaseUnit || unit.archiveData.archived ? undefined : onArchive}
              unarchive={unit.archiveData.archived ? onUnarchive : undefined}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  archivedField: {
    opacity: 0.6,
  },
  baseUnitText: {
    color: GRAY_DARKER,
    fontStyle: 'italic',
  },
  iconStyle: {
    color: GRAY_DARKER,
    marginRight: '10px',
    height: '12px',
    width: '12px',
  },
  rowContainer: {
    display: 'flex',
    width: '100%',
    position: 'relative',
  },
  tableRow: {
    alignItems: 'center',
    borderTop: 1,
    borderColor: GRAY_LIGHT,
    display: 'flex',
    marginRight: -25,
    padding: '10px 14px',
    width: '100%',
  },
  conversionFactorInput: {
    paddingRight: 82,
    display: 'flex',
    alignItems: 'center',
  },
  conversionFactorInputField: {
    flex: 3,
  },
  conversionFactorInputFlex1: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  highlight: {
    background: 'rgba(235, 135, 21, 0.2)',
  },
});
